import type { AdminUser } from '@hemmafixa/shared';
import {
  Avatar,
  Badge,
  Button,
  Card,
  DescriptionList,
  DetailLayout,
  DetailSection,
  EmptyState,
  FormField,
  InlinePanel,
  InlinePanelFooter,
  StatusBadge,
  Textarea,
  toast,
} from '@hemmafixa/ui';
import { SearchX } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { useI18n } from '@/i18n/context';
import type { Messages } from '@/i18n/sv';
import { ApiError } from '@/lib/api';
import { useCurrentUser } from '../auth/api';
import { BackLink } from '../shared/back-link';
import { QueryView } from '../shared/query-view';
import { useStatuses } from '../shared/statuses';
import { useAdminUser, useUserStatusChange } from './api';
import { AuditList } from './audit-list';

function actionError(error: unknown, t: Messages) {
  if (error instanceof ApiError && error.code === 'CONFLICT') return t.admin.users.conflict;
  if (error instanceof ApiError && error.status === 0) return t.common.errorMessage;
  return t.admin.users.genericError;
}

/** One account: details, suspend or reactivate, and what happened to it (P2-10). */
export function UserPage() {
  const { id = '' } = useParams();
  const { t } = useI18n();
  return (
    <div className="grid gap-4">
      <BackLink to="/admin/users" label={t.nav.users} />
      <AdminUserDetail id={id} />
    </div>
  );
}

/** Loads and shows one account; the full page and the list's details panel both use it. */
export function AdminUserDetail({ id }: { id: string }) {
  const { t } = useI18n();
  const query = useAdminUser(id);
  return (
    <QueryView query={query} loading="detail">
      {(user) =>
        !user ? (
          <EmptyState icon={SearchX} title={t.common.notFoundTitle} description={t.common.notFoundText} />
        ) : (
          <UserDetail user={user} />
        )
      }
    </QueryView>
  );
}

function UserDetail({ user }: { user: AdminUser }) {
  const { t, date, relative } = useI18n();
  const statuses = useStatuses();
  const me = useCurrentUser();
  const [panel, setPanel] = useState<'suspend' | 'reactivate' | null>(null);
  const u = t.admin.users;
  const isMe = me.id === user.id;

  return (
    <>
      <Card className="grid gap-4 p-5">
        {/* Name on the left, the key facts beside it, so the card stays short */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
          <div className="flex min-w-0 flex-1 basis-64 items-center gap-4">
            <Avatar seed={user.id} name={user.name} size="lg" />
            <div className="grid min-w-0 gap-0.5">
              <h1 className="truncate text-2xl font-bold text-secondary" title={user.name}>
                {user.name}
              </h1>
              <p className="truncate text-sm text-muted-foreground">{user.email}</p>
            </div>
          </div>
          <dl className="flex flex-wrap gap-x-8 gap-y-3">
            {[
              {
                label: u.columns.status,
                value: <StatusBadge status={user.status} statuses={statuses.user} />,
              },
              { label: u.created, value: date(user.createdAt) },
              {
                label: u.columns.lastLogin,
                value: user.lastLoginAt ? relative(user.lastLoginAt) : u.neverLoggedIn,
              },
            ].map((fact) => (
              <div key={fact.label} className="grid gap-1.5">
                <dt className="text-xs text-muted-foreground">{fact.label}</dt>
                <dd className="text-sm font-medium text-secondary">{fact.value}</dd>
              </div>
            ))}
          </dl>
        </div>
        {panel ? (
          <StatusPanel user={user} action={panel} onClose={() => setPanel(null)} />
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
            {user.status === 'suspended' && (
              <p className="min-w-0 flex-1 basis-56 rounded-xl bg-muted px-4 py-2.5 text-sm text-danger">
                {u.suspendedNotice}
              </p>
            )}
            {isMe ? (
              <p className="text-sm text-muted-foreground">{u.ownAccount}</p>
            ) : user.status === 'active' ? (
              <Button
                variant="outline"
                size="lg"
                className="ml-auto text-danger"
                onClick={() => setPanel('suspend')}
              >
                {u.suspend}
              </Button>
            ) : (
              <Button size="lg" className="ml-auto" onClick={() => setPanel('reactivate')}>
                {u.reactivate}
              </Button>
            )}
          </div>
        )}
      </Card>
      <DetailLayout
        aside={
          <DetailSection title={u.contact} collapsible>
            <DescriptionList
              items={[
                {
                  label: u.columns.email,
                  value: (
                    <a
                      className="break-all font-medium underline underline-offset-2"
                      href={`mailto:${user.email}`}
                    >
                      {user.email}
                    </a>
                  ),
                },
                {
                  label: u.phone,
                  value: user.phone ? (
                    <a className="font-medium underline underline-offset-2" href={`tel:${user.phone}`}>
                      {user.phone}
                    </a>
                  ) : (
                    '–'
                  ),
                },
              ]}
            />
          </DetailSection>
        }
      >
        <DetailSection title={u.account}>
          <DescriptionList
            items={[
              {
                label: u.columns.roles,
                value: (
                  <div className="flex flex-wrap gap-1">
                    {user.roles.map((role) => (
                      <Badge key={role}>{t.areas[role]}</Badge>
                    ))}
                  </div>
                ),
              },
              ...(user.adminPermissions.length > 0
                ? [
                    {
                      label: u.permissions,
                      value: user.adminPermissions.map((p) => u.permissionNames[p]).join(', '),
                    },
                  ]
                : []),
              { label: u.language, value: u.languages[user.locale] },
              { label: u.password, value: user.hasPassword ? u.passwordSet : u.passwordNotSet },
              { label: u.sessions, value: String(user.activeSessions) },
              { label: u.jobs, value: String(user.jobCount) },
              ...(user.provider
                ? [
                    {
                      label: u.company,
                      value: (
                        <span className="flex flex-wrap items-center gap-2">
                          <Link
                            to={`/admin/providers/${user.provider.id}`}
                            className="font-medium underline underline-offset-2"
                          >
                            {user.provider.companyName ?? u.openProvider}
                          </Link>
                          <StatusBadge status={user.provider.status} statuses={statuses.provider} />
                        </span>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        </DetailSection>
        <DetailSection title={u.activity}>
          <AuditList entityType="user" entityId={user.id} />
        </DetailSection>
      </DetailLayout>
    </>
  );
}

/** Suspending needs a reason (kept in the audit log); reactivating takes an optional note. */
function StatusPanel({
  user,
  action,
  onClose,
}: {
  user: AdminUser;
  action: 'suspend' | 'reactivate';
  onClose: () => void;
}) {
  const { t } = useI18n();
  const u = t.admin.users;
  const change = useUserStatusChange(user.id);
  const [text, setText] = useState('');
  const [error, setError] = useState<string>();
  const suspend = action === 'suspend';

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (suspend && text.trim().length < 3) {
      setError(u.reasonTooShort);
      return;
    }
    change.mutate(suspend ? { action, reason: text.trim() } : { action, note: text.trim() || undefined }, {
      onSuccess: () => {
        onClose();
        toast.success(suspend ? u.suspendedToast : u.reactivatedToast);
      },
      onError: (failure) => toast.error(actionError(failure, t)),
    });
  };

  return (
    <InlinePanel
      title={suspend ? u.suspendTitle : u.reactivateTitle}
      description={suspend ? u.suspendText : u.reactivateText}
      onClose={onClose}
      onSubmit={submit}
      closeLabel={t.common.cancel}
    >
      <FormField label={suspend ? u.reason : u.note} error={error} required={suspend}>
        <Textarea rows={3} maxLength={500} value={text} onChange={(event) => setText(event.target.value)} />
      </FormField>
      <InlinePanelFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t.common.cancel}
        </Button>
        <Button type="submit" variant={suspend ? 'danger' : 'primary'} loading={change.isPending}>
          {suspend ? u.suspend : u.reactivate}
        </Button>
      </InlinePanelFooter>
    </InlinePanel>
  );
}
